import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { useShareGate } from "@/hooks/useShareGate";
import { useState, useCallback, useEffect, useRef } from "react";
import { useRunToken } from "@/hooks/useRunToken";
import { Button } from "@/components/ui/button";
import { ScanText, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { OCR_LANGUAGES, ocrParagraphs } from "../pdf/ocrText";
import { ProcessingState } from "@/components/ToolLayout";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Extracts readable text from an image or scanned document using Optical Character Recognition, turning a picture of text into text you can actually select, copy, and edit. Runs entirely in your browser — the image never leaves your device.`,
  how: `Upload or paste (Ctrl/Cmd+V) an image containing text (JPG, PNG, WebP or an iPhone HEIC photo), choose the language of the text if it is not English, and click Extract Text. Copy the result or download it as a .txt file.`,
  tip: `Clear, high-contrast, well-lit scans produce far more accurate results than a blurry photo or a low-resolution image — if accuracy matters, it's worth re-scanning a source document rather than working from a rough phone photo. This reads images specifically, not PDFs — for a scanned PDF, use the PDF OCR tool instead, which handles multi-page documents properly.`,
};

export default function OcrTool() {
  const { gateShare } = useShareGate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultText, setResultText] = useState("");
  const [language, setLanguage] = useState("eng");
  const [rawText, setRawText] = useState("");
  const [keepBreaks, setKeepBreaks] = useState(false);
  const [confidence, setConfidence] = useState<number | null>(null);
  const runs = useRunToken();
  useEffect(() => () => runs.cancel(), [runs]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const handleFilesRef = useRef<(files: File[]) => void>(() => {});
  // Paste a screenshot (Ctrl/Cmd+V) anywhere on the page.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const img = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith("image/"));
      if (img) { e.preventDefault(); handleFilesRef.current([img]); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    // The accept prop is only a picker hint; a drag-and-drop can still deliver any file, so check.
    const isImage = f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif|tiff?)$/i.test(f.name);
    if (!isImage) {
      toast.error(`Wrong file type (${f.type || f.name.split(".").pop() || "unknown"}). This tool reads image files (JPG, PNG, WebP, HEIC ...). For a scanned PDF use OCR PDF.`);
      return;
    }
    runs.cancel();
    setProcessing(false);
    try {
      // iPhone photos (HEIC) become JPEG, and a photo taken sideways is turned upright: OCR cannot read rotated text.
      const [{ convertHeicToJpegFile }, { normalizeImageFile }] = await Promise.all([import("../pdf/operations"), import("../pdf/imagePdf")]);
      const ready = await normalizeImageFile(await convertHeicToJpegFile(f));
      setFile(ready);
      setPreview(URL.createObjectURL(ready));
      setResultText("");
      setConfidence(null);
    } catch {
      toast.error("This image could not be read. It may be damaged or in a format the browser cannot open.");
    }
  }, [runs]);
  handleFilesRef.current = handleFiles;

  const extractText = async () => {
    if (!file) return;
    const run = runs.start();
    setProcessing(true);
    setProgress(0);
    setResultText("");
    setRawText("");
    setConfidence(null);
    try {
      const { startOcrEngine } = await import("../pdf/ocrEngine");
      const engine = await startOcrEngine(language, (f) => runs.isCurrent(run) && setProgress(f));
      try {
        const data = await engine.read(file, { isCurrent: () => runs.isCurrent(run), autoRotate: true });
        if (!runs.isCurrent(run)) return;
        const paragraphs = ocrParagraphs(data);
        const text = (paragraphs.length ? paragraphs.join("\n\n") : (data.text ?? "").trim());
        setResultText(text);
        setRawText((data.text ?? "").replace(/\n{3,}/g, "\n\n").trim());
        setConfidence(typeof data.confidence === "number" ? Math.round(data.confidence) : null);
        toast.success(text ? "Text extracted!" : "No readable text was found in this image.");
      } finally {
        await engine.terminate();
      }
    } catch (err) {
      if (!runs.isCurrent(run) || (err instanceof Error && err.message === "cancelled")) return;
      console.error("OCR failed:", err);
      toast.error(err instanceof Error && err.name === "OcrStartError" ? err.message : "Couldn't process this image. Try a clearer image.");
    } finally {
      if (runs.isCurrent(run)) setProcessing(false);
    }
  };

  const shownText = keepBreaks && rawText ? rawText : resultText;
  const downloadText = () => {
    downloadBlob(new Blob([shownText], { type: "text/plain;charset=utf-8" }), `${(file?.name ?? "text").replace(/\.[^.]+$/, "")}.txt`);
  };

  const copyText = () => gateShare(async () => {
    try { await navigator.clipboard.writeText(shownText); toast.success("Copied!"); return true; }
    catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; }
  });

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image with text" description="Extract text from images using OCR. You can also paste a screenshot with Ctrl/Cmd+V." />
      ) : (
        <div className="space-y-4">
          <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg border" />

          {!processing && (
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="ocr-lang" className="text-sm text-muted-foreground">Language of the text</label>
              <select id="ocr-lang" value={language} onChange={(e) => { setLanguage(e.target.value); setResultText(""); }} className="h-9 rounded-md border bg-background px-2 text-sm">
                {OCR_LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
              <p className="basis-full text-xs text-muted-foreground">Accented text (French, Spanish, German and so on) needs that language selected, or the accents are lost. Codes and serial numbers with look-alike characters (0/O, 1/l/I) are unreliable, so check them by eye.</p>
            </div>
          )}
          {processing && <ProcessingState message={`Reading text... ${Math.round(progress * 100)}% (the first time, the language data is downloaded)`} progress={progress * 100} onStop={() => { runs.cancel(); setProcessing(false); }} />}

          {resultText && (
            <div className="p-4 rounded-lg border bg-card space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Extracted Text</p>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer"><input type="checkbox" checked={keepBreaks} onChange={(e) => setKeepBreaks(e.target.checked)} /> Keep original line breaks (good for lists, tables and addresses)</label>
              <p className="text-sm whitespace-pre-wrap">{shownText}</p>
              {confidence !== null && confidence < 70 && <NoticeBox className="text-xs">The reading is uncertain ({confidence}% confidence). Check the text, and try a clearer image or the right language.</NoticeBox>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copyText} className="gap-1.5"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                <Button size="sm" variant="outline" onClick={downloadText} className="gap-1.5"><Download className="h-3.5 w-3.5" /> Download .txt</Button>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={extractText} disabled={processing} className="gap-2">
              <ScanText className="h-4 w-4" /> {processing ? "Reading..." : "Extract Text"}
            </Button>
            <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setResultText(""); setConfidence(null); }} disabled={processing}>Reset</Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
